/**
 * useFollow Hook
 * Manages following/unfollowing users
 * Shared between web and mobile
 */

import { useCallback, useState } from 'react';
import type { HttpClient } from '../api/httpClient';

// =============================================================================
// Types
// =============================================================================

export interface UseFollowOptions {
  /** HTTP client for API calls */
  httpClient: HttpClient;
  /** Target user ID to follow/unfollow */
  targetUserId: string;
  /** Whether current user is authenticated */
  isAuthenticated: boolean;
}

export interface UseFollowResult {
  isFollowing: boolean | null; // null = not loaded yet
  loading: boolean;
  error: string | null;
  followUser: () => Promise<void>;
  unfollowUser: () => Promise<void>;
  checkFollowStatus: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useFollow(options: UseFollowOptions): UseFollowResult {
  const { httpClient, targetUserId, isAuthenticated } = options;

  const [isFollowing, setIsFollowing] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if current user follows the target user
  const checkFollowStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setIsFollowing(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await httpClient.get<{ isFollowing: boolean }>(
        `/api/leaderboard/follow-status/${targetUserId}`,
      );
      setIsFollowing(data.isFollowing);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check follow status');
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, targetUserId]);

  // Follow the target user
  const followUser = useCallback(async () => {
    if (!isAuthenticated) {
      setError('You must be logged in to follow users');
      return;
    }

    setLoading(true);
    setError(null);
    setIsFollowing(true); // Optimistic update

    try {
      await httpClient.post(`/api/leaderboard/follow/${targetUserId}`);
    } catch (err) {
      setIsFollowing(false); // Revert on error
      setError(err instanceof Error ? err.message : 'Failed to follow user');
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, targetUserId]);

  // Unfollow the target user
  const unfollowUser = useCallback(async () => {
    if (!isAuthenticated) {
      setError('You must be logged in to unfollow users');
      return;
    }

    setLoading(true);
    setError(null);
    setIsFollowing(false); // Optimistic update

    try {
      await httpClient.deleteNoContent(`/api/leaderboard/follow/${targetUserId}`);
    } catch (err) {
      setIsFollowing(true); // Revert on error
      setError(err instanceof Error ? err.message : 'Failed to unfollow user');
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, targetUserId]);

  return {
    isFollowing,
    loading,
    error,
    followUser,
    unfollowUser,
    checkFollowStatus,
  };
}
